import type { AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react'
import { Link, type LinkProps } from 'react-router-dom'

/**
 * Primary CTA. Maps to Stitch's `.btn-primary-magnetic` / `cta-btn` and the
 * current site's `.magnetic-btn` — same amber gradient pill, same hover glow.
 * Pointer-follow "magnetic" motion (JS mousemove transform) is intentionally
 * NOT wired here; per master prompt §8 that's a nice-to-have on fine-pointer
 * devices only and must respect prefers-reduced-motion — added in Phase 2
 * alongside the other motion tokens, not as a base button behavior.
 */
export const glowClasses =
  'inline-flex items-center justify-center gap-2 rounded-pill px-6 py-2.5 font-sans text-sm font-bold uppercase tracking-wide ' +
  'bg-gradient-to-r from-accent-amber-light to-accent-amber text-accent-amber-foreground ' +
  'transition-all duration-normal hover:scale-105 hover:shadow-[0_0_20px_rgba(255,185,99,0.4)] active:scale-95'

export function GlowButton({
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button className={`${glowClasses} ${className}`} {...props} />
}

export function GlowLink({
  className = '',
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement>) {
  return <a className={`${glowClasses} ${className}`} {...props} />
}

/**
 * Same visual as `GlowLink` but a real router `<Link>` — for internal routed
 * paths (e.g. `/kontakt`) where a plain `<a>` would force a full page
 * reload instead of a client-side navigation. `GlowLink` stays for
 * external/hash-anchor uses.
 */
export function GlowRouterLink({ className = '', ...props }: LinkProps) {
  return <Link className={`${glowClasses} ${className}`} {...props} />
}

/** Outline/secondary CTA — matches `.btn-outline` on the current site. */
export function SecondaryLink({
  className = '',
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a
      className={
        'inline-flex items-center justify-center gap-2 rounded-pill px-6 py-2.5 font-sans text-sm font-semibold ' +
        'border border-border-strong text-foreground transition-all duration-normal ' +
        'hover:border-accent-fire hover:text-accent-fire hover:scale-105 active:scale-95 ' +
        className
      }
      {...props}
    />
  )
}
